import {GRID_TYPE} from 'eventbrite_design_system/mediaCardContent/constants';

import {lazyGettext} from '../translations';

import {
    // bucket keys
    BEST_OF_CITY_EVENTS,
    BUSINESS_AND_PROFESSIONAL_EVENTS,
    CHARITY_AND_CAUSES_EVENTS,
    COMMUNITY_AND_CULTURE_EVENTS,
    DISCOUNTED_EVENTS,
    FAMILY_AND_EDUCATION_EVENTS,
    FILM_MEDIA_AND_ENTERTAINMENT_EVENTS,
    FIVE_TO_FOLLOW_IN_FOOD_AND_DRINK,
    FIVE_TO_FOLLOW_IN_HEALTH_AND_WELLNESS,
    FIVE_TO_FOLLOW_IN_MUSIC,
    FOLLOWED_PROFILES,
    FOOD_AND_DRINK_EVENTS,
    FREE_EVENTS,
    FROM_FOLLOWED_ORGANIZER,
    HEALTH_AND_WELLNESS_EVENTS,
    HOBBIES_AND_SPECIAL_INTEREST_EVENTS,
    LIKED_EVENTS,
    MUSIC_EVENTS,
    NEARBY_CITY_EVENTS,
    NEW_UNDERPERFORMING_EVENTS,
    ORGANIZERS_TO_FOLLOW,
    PERFORMING_AND_VISUAL_ARTS_EVENTS,
    PERSONALIZED_FOOD_AND_DRINK_EVENTS,
    PERSONALIZED_MUSIC_EVENTS,
    POPULAR_EVENTS,
    POPULAR_PROFILES,
    RELIGION_AND_SPIRITUALITY_EVENTS,
    SCIENCE_AND_TECHNOLOGY_EVENTS,
    SMALL_CITY_FALLBACK,
    THIS_WEEKEND,
    USER_SAVED_EVENTS,

    // feature flags
    ENABLE_PREMIUM_BUCKET,
    LAUNCH_FIVE_TO_FOLLOW_BUCKET,
} from './constants';

const FEED_CONFIG = {
    [ORGANIZERS_TO_FOLLOW]: {
        order: 1,
        orderAfter: [
            FROM_FOLLOWED_ORGANIZER,
            FOLLOWED_PROFILES,
            BEST_OF_CITY_EVENTS,
            POPULAR_EVENTS,
            NEARBY_CITY_EVENTS,
            SMALL_CITY_FALLBACK,
        ],
        propsOverwrite: {
            name: lazyGettext('Organizers to follow'),
        },
        layout: {
            desktop: {
                events: {
                    bucketClassNames: 'organizers-bucket__content eds-l-pad-bot-8',
                },
            },
            mobile: {
                events: {
                    bucketClassNames: 'organizers-bucket__content eds-l-pad-bot-8',
                },
            },
        },
    },
    [FIVE_TO_FOLLOW_IN_FOOD_AND_DRINK]: {
        order: 5,
        orderAfter: [FOOD_AND_DRINK_EVENTS],
        featureFlagName: LAUNCH_FIVE_TO_FOLLOW_BUCKET,
        layout: {
            desktop: {
                events: {
                    bucketClassNames: 'five-to-follow-bucket__content eds-l-pad-bot-8',
                },
            },
            mobile: {
                events: {
                    bucketClassNames: 'five-to-follow-bucket__content eds-l-pad-bot-8',
                },
            },
        },
    },
    [FIVE_TO_FOLLOW_IN_HEALTH_AND_WELLNESS]: {
        order: 8,
        orderAfter: [HEALTH_AND_WELLNESS_EVENTS],
        featureFlagName: LAUNCH_FIVE_TO_FOLLOW_BUCKET,
        layout: {
            desktop: {
                events: {
                    bucketClassNames: 'five-to-follow-bucket__content eds-l-pad-bot-8',
                },
            },
            mobile: {
                events: {
                    bucketClassNames: 'five-to-follow-bucket__content eds-l-pad-bot-8',
                },
            },
        },
    },
    [FIVE_TO_FOLLOW_IN_MUSIC]: {
        order: 3,
        orderAfter: [MUSIC_EVENTS],
        featureFlagName: LAUNCH_FIVE_TO_FOLLOW_BUCKET,
        layout: {
            desktop: {
                events: {
                    bucketClassNames: 'five-to-follow-bucket__content eds-l-pad-bot-8',
                },
            },
            mobile: {
                events: {
                    bucketClassNames: 'five-to-follow-bucket__content eds-l-pad-bot-8',
                },
            },
        },
    },
    [LIKED_EVENTS]: {
        order: 1,
        orderAfter: [ORGANIZERS_TO_FOLLOW, FROM_FOLLOWED_ORGANIZER, FOLLOWED_PROFILES, POPULAR_EVENTS],
        isDynamicSeeMore: true,
    },
    [USER_SAVED_EVENTS]: {
        hide: true,
    },
    [SMALL_CITY_FALLBACK]: {
        layout: {
            desktop: {
                events: {
                    max: 32,
                },
            },
            mobile: {
                events: {
                    max: 32,
                },
            },
        },
    },
    [BEST_OF_CITY_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('Our editors\' picks'),
        },
        overwrite: {
            featureFlagName: ENABLE_PREMIUM_BUCKET,
            props: {
                propsOverwrite: {
                    name: lazyGettext('Our editors\' picks'),
                },
                layout: {
                    desktop: {
                        events: {
                            bucketClassNames: 'premium-events-bucket__content eds-l-pad-bot-8',
                            layout: {
                                default: {
                                    containerClasses: [
                                        'feed__card-cell premium-card',
                                    ],
                                },
                            },
                        },
                    },
                    mobile: {
                        events: {
                            bucketClassNames: 'premium-events-bucket__content eds-l-pad-bot-8',
                            layout: {
                                default: {
                                    containerClasses: [
                                        'feed__card-cell premium-card',
                                    ],
                                },
                            },
                            propsOverwrite: {
                                default: {
                                    type: GRID_TYPE,
                                },
                            },
                        },
                    },
                },
            },
        },
    },
    [POPULAR_EVENTS]: {
        overwrite: {
            featureFlagName: ENABLE_PREMIUM_BUCKET,
            props: {
                layout: {
                    desktop: {
                        events: {
                            bucketClassNames: 'premium-events-bucket--alternative__content eds-l-pad-bot-8',
                            layout: {
                                default: {
                                    containerClasses: [
                                        'feed__card-cell eds-g-cell eds-g-cell--has-overflow eds-g-cell-4-12',
                                    ],
                                },
                            },
                        },
                    },
                },
            },
        },
    },
    [THIS_WEEKEND]: {
        propsOverwrite: {
            name: lazyGettext('Happening this weekend'),
        },
    },
    [MUSIC_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('The best shows in town'),
        },
    },
    [PERSONALIZED_MUSIC_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('Your music lineup'),
        },
    },
    [FOOD_AND_DRINK_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('Food & drink events'),
        },
    },
    [PERSONALIZED_FOOD_AND_DRINK_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('Food & drink events you\'ll love'),
        },
    },
    [HEALTH_AND_WELLNESS_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('Treat yourself right: health and wellness events'),
        },
    },
    [PERFORMING_AND_VISUAL_ARTS_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('Explore the arts'),
        },
    },
    [BUSINESS_AND_PROFESSIONAL_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('Enrich your career'),
        },
    },
    [DISCOUNTED_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('Discounted events'),
        },
    },
    [FILM_MEDIA_AND_ENTERTAINMENT_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('For film lovers'),
        },
    },
    [SCIENCE_AND_TECHNOLOGY_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('Learn something new'),
        },
    },
    [FAMILY_AND_EDUCATION_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('Family-friendly experiences'),
        },
    },
    [FREE_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('No excuse to stay in: free events'),
        },
    },
    [FROM_FOLLOWED_ORGANIZER]: {
        propsOverwrite: {
            name: lazyGettext('Your followed events'),
        },
    },
    [CHARITY_AND_CAUSES_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('Make a difference'),
        },
    },
    [COMMUNITY_AND_CULTURE_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('Get involved in your community'),
        },
    },
    [RELIGION_AND_SPIRITUALITY_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('Connect with your spirituality'),
        },
    },
    [NEW_UNDERPERFORMING_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('Don\'t miss these new events'),
        },
    },
    [HOBBIES_AND_SPECIAL_INTEREST_EVENTS]: {
        propsOverwrite: {
            name: lazyGettext('Get a new hobby'),
        },
    },
    [POPULAR_PROFILES]: {
        hide: true,
    },
};

export default FEED_CONFIG;
